import React, { useState, useCallback, useRef } from 'react';
import { Form, Card, Space, DatePicker, Input, message, Segmented } from 'antd';
import FormItem from '@/common/FormItem';
import styles from '@/common/Layout/index.module.less';
import CustomTable from 'common/Content/CustomTable';
import CustomButton from '@/common/CustomButton';
import Layout from '@/common/Layout';
import { history } from 'app/history';
import { useGetUacrOfSamplingOrDbarsQuery } from '../../redux/apiSlice';
import moment from 'moment';
import Spinner from '../../../../common/Spinner';
import { EditOutlined, PrinterOutlined } from '@ant-design/icons';
import LocalStorage from 'utilities/localStorage';
const { RangePicker } = DatePicker;

const AdminPage = () => {
  const [form] = Form.useForm();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  // 'final' shows finalized records (default); 'draft' shows unfinished drafts,
  // resumed via the existing Edit action.
  const [viewMode, setViewMode] = useState('final');
  const debounceTimer = useRef(null);

  const isDraftView = viewMode === 'draft';

  const { data, error, isLoading } = useGetUacrOfSamplingOrDbarsQuery({
    page,
    per_page: perPage,
    from_date: fromDate,
    to_date: toDate,
    search: searchText,
    is_draft: isDraftView
  });

  const totalRecords = data?.pagination?.total_records || 0;

  const columns = [
    {
      title: 'Area (Equipment ID)',
      dataIndex: 'area',
      key: 'area',
      ellipsis: true,
      render: (_, record) => {
        const master = record?.sampling_dispensing_booth_area_master;
        if (!master) return '';
        return master.description
          ? `${master.description} (${master.area})`
          : master.area;
      }
    },
    {
      title: 'Product Batch No. / In-House Batch No.',
      dataIndex: 'laf_product_batch_no',
      key: 'laf_product_batch_no',
      ellipsis: true,
      render: (_, record) =>
        record?.laf_product_batch_no_display ||
        record?.laf_product_batch_no ||
        ''
    },
    {
      title: 'Activities',
      dataIndex: 'activityCount',
      key: 'activityCount',
      width: '100px',
      align: 'center',
      render: (_, record) => record?.uacr_cleaning_activity_logs?.length || 1
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '150px',
      render: (_, record) =>
        record?.createdAt ? (
          <a href={`uacr-of-sampling-or-dbar-list/${record?.id}`}>
            {moment(record.createdAt).format('DD-MMM-YYYY')}
          </a>
        ) : (
          'N/A'
        )
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      width: '80px',
      render: (_, record) => (
        <EditOutlined
          onClick={() =>
            history.push(`/uacr-of-sampling-or-dbar-create?id=${record?.id}`)
          }
        />
      )
    }
  ];

  const handleSearch = useCallback((e) => {
    const value = e.target.value;
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setSearchText(value);
      setPage(1);
    }, 400);
  }, []);

  const handlePrint = useCallback(async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select at least one record to print');
      return;
    }
    setIsPrinting(true);
    try {
      const user = LocalStorage.getItem('Gravity_user');
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${baseUrl.replace(/\/?$/, '/')}usage-cleaning-log-book-pdf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user?.token}`,
            'x-session-token': user?.session_token
          },
          body: JSON.stringify({ ids: selectedRowKeys })
        }
      );
      if (!response.ok) {
        throw new Error('PDF generation failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `UACR_Cleaning_Log_${moment().format('DD-MMM-YYYY')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('PDF downloaded successfully');
    } catch (err) {
      console.log(err);
      message.error('Failed to generate PDF');
    } finally {
      setIsPrinting(false);
    }
  }, [selectedRowKeys]);

  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      setFromDate(moment(dates[0]).startOf('day').format());
      setToDate(moment(dates[1]).endOf('day').format());
    } else {
      setFromDate('');
      setToDate('');
    }
    setPage(1);
  };

  let content;
  if (isLoading) {
    content = <Spinner />;
  } else if (data) {
    content = (
      <Layout sider={false}>
        <Form form={form} layout="vertical">
          <Card
            title={
              <span>
                Usage and Cleaning Record of Sampling / Dispensing Booth And
                Room
              </span>
            }
            className={`${styles.cardStyles_approver}`}
            extra={
              <Space>
                <Segmented
                  value={viewMode}
                  onChange={(val) => {
                    setViewMode(val);
                    setPage(1);
                    setSelectedRowKeys([]);
                  }}
                  options={[
                    { label: 'Final', value: 'final' },
                    { label: 'Drafts', value: 'draft' }
                  ]}
                />
                <Input
                  placeholder="Search by Area / Description"
                  allowClear
                  onChange={handleSearch}
                  style={{ width: 250 }}
                />
                <RangePicker onChange={handleDateChange} allowClear />
                {!isDraftView && (
                  <CustomButton
                    className={styles.inwardButton}
                    onClick={handlePrint}
                    type="primary"
                    loading={isPrinting}
                    disabled={selectedRowKeys.length === 0}
                    icon={<PrinterOutlined />}
                  >
                    Print ({selectedRowKeys.length})
                  </CustomButton>
                )}
                <CustomButton
                  className={styles.inwardButton}
                  onClick={() =>
                    history.push('/uacr-of-sampling-or-dbar-create')
                  }
                  type="primary"
                >
                  Create
                </CustomButton>
              </Space>
            }
          >
            <div className={`${styles.table_container}`}>
              <CustomTable
                rowKey="id"
                columns={columns}
                dataSource={data.data}
                rowSelection={
                  isDraftView
                    ? undefined
                    : {
                        selectedRowKeys,
                        onChange: (keys) => setSelectedRowKeys(keys),
                        preserveSelectedRowKeys: true
                      }
                }
                showPaginationMeta
                pagination={{
                  size: 'small',
                  pageSize: perPage,
                  current: page,
                  total: totalRecords,
                  onChange: (newPage, pageSize) => {
                    setPage(newPage);
                    setPerPage(pageSize);
                  }
                }}
              />
            </div>
          </Card>
          <br />
          <FormItem className="text-center">
            <CustomButton
              className={styles.inwardButton}
              onClick={() => history.push('log-books')}
              type="primary"
            >
              Back to Home
            </CustomButton>
          </FormItem>
        </Form>
      </Layout>
    );
  } else if (error) {
    console.log(error);
    content = 'Something went wrong!';
  }

  return <>{content}</>;
};
export default AdminPage;
